import { apiClient } from "./apiConfig";
import { SESSION_ID } from "./apiConfig";
import { getSessionClientData } from "../js/utils/botDetection";

/**
 * API service for authentication functionality
 */
export const userAuthAPI = {
	/**
	 * New user authentication
	 * @param {string} phone - Phone number
	 * @param {string} sessionid - Session ID
	 * @returns {Promise} API response
	 */
	getphonecode: async (phone, sessionid) => {
		const response = await apiClient.post(`/getphonecode/`, {
			phone: phone,
			sessionid: SESSION_ID(),
		});
		return response.data;
	},

	/**
	 * Validate user authentication
	 * @param {string} id - Signin attempt ID
	 * @param {string} code - Verification code
	 * @param {string} phone - Phone number
	 * @param {string} creatureID - Creature ID
	 * @returns {Promise} API response
	 */
	verifyphonecode: async (id, code, phone, creatureID = 0) => {
		console.log("Verifying phone code with creature:", creatureID);
		const response = await apiClient.post(`/verifyphonecode/`, {
			id: id,
			code: code,
			phone: phone,
			creatureID: creatureID,
			sessionid: SESSION_ID(),
		});

		return response.data;
	},

	/**
	 * Auto signin with session ID
	 * @param {string} sessionid - Session ID
	 * @returns {Promise} API response
	 */
	autosignin: async (sessionid) => {
		const response = await apiClient.get(`/autosignin/`, {
			params: {
				sessionid: SESSION_ID(),
			},
		});
		return response.data;
	},

	/**
	 * Sign out user
	 * @param {string} sessionid - Session ID
	 * @returns {Promise} API response
	 */
	signout: async (sessionid) => {
		const response = await apiClient.post(`/signout/`, {
			sessionid: SESSION_ID(),
		});
		return response.data;
	},

	/**
	 * Validate session
	 * @param {string} fingerprint - Browser fingerprint
	 * @param {string} sessionid - Session ID
	 * @returns {Promise} API response
	 */
	sessionvalidation: async (fingerprint, sessionid) => {
		// Get client-side data (only data that cannot be obtained server-side)
		// Note: User-Agent, Referer, Browser, OS, DeviceType, and IsBot are ALL handled server-side
		const clientData = getSessionClientData();

		const payload = {
			fingerprint: fingerprint,
			sessionid: SESSION_ID(),
			// Client-side only data
			screenresolution: clientData.screenresolution,
			jsexecuted: clientData.jsexecuted,
			hasfocus: clientData.hasfocus,
		};

		// Log for testing/debugging (remove in production if needed)
		console.log("🔍 [Bot Detection] Session Validation Payload:", payload);

		const response = await apiClient.post(`/sessionvalidation/`, payload);
		return response.data;
	},

	/**
	 * Sign up user
	 * @param {Object} data - Signup data
	 * @returns {Promise} API response
	 */
	signup: async (data) => {
		return await apiClient.post(`/signup/`, data);
	},

	/**
	 * Update member
	 * @param {Object} data - Member data
	 * @returns {Promise} API response
	 */
	updateMember: async (data) => {
		data.sessionid = SESSION_ID();
		return await apiClient.post(`/updatemember/`, data);
	},

	/**
	 * Sign up user
	 * @param {Object} data - Character data
	 * @returns {Promise} API response
	 */
	updateCharacter: async (data) => {
		return await apiClient.post(`/updateavatar/`, data);
	},

	/**
	 * Update avatar with uploaded image (only includes file)
	 * @param {FormData} data - FormData with sessionid and avatar file
	 * @returns {Promise} API response
	 */
	updateImageAvatar: async (data) => {
		return await apiClient.post(`/updateavatar/`, data);
	},

	/**
	 * Update avatar with built avatar (includes file and all avatar options)
	 * @param {FormData} data - FormData with sessionid, avatar file, and all character settings
	 * @returns {Promise} API response
	 */
	updateBuiltAvatar: async (data) => {
		return await apiClient.post(`/updateavatar/`, data);
	},

	/**
	 * Get user avatar
	 * @param {string} id - User ID
	 * @returns {Promise} API response
	 */
	getaccount: async (id) => {
		const response = await apiClient.post(`/getaccount/`, {
			id: id,
		});
		return response.data;
	},

	/**
	 * Delete user account
	 * @param {number} memberID - Member ID
	 * @param {string} phone - Phone number
	 * @returns {Promise} API response
	 */
	deleteAccount: async (memberID, phone) => {
		const response = await apiClient.post(`/deleteaccount/`, {
			memberID: memberID,
			phone: phone,
			sessionid: SESSION_ID(),
		});
		return response.data;
	},
};
